<template>
  <div :class="bem({ open })">
    <template v-if="props.allowQtyChange && props.currentQty && !props.soldOut">
      <button
        :class="bem('trigger-button')"
        @click="handleClick"
      >
        {{ props.currentQty }}
        <VisionIcon
          icon="caret"
          :rotate="open ? 270 : 90"
          size="xx-small"
          colour="black"
        />
      </button>
      <div :class="bem('selector-clip')">
        <div :class="bem('selector-container')">
          <button
            :class="bem('qty-button', { minus: true })"
            @click="handleQtyChange(props.currentQty - 1)"
          >
            <VisionIcon
              :icon="showRemoveBtn ? 'cross' : 'minus'"
              size="xx-small"
              colour="black"
            />
          </button>
          <label
            :for="inputId"
            :class="bem('selector-label')"
          >
            <input
              :id="inputId"
              :value="props.currentQty"
              type="number"
              :max="25"
              @change="handleSelect($event)"
            />
          </label>
          <button
            :class="bem('qty-button')"
            @click="handleQtyChange(props.currentQty + 1)"
          >
            <VisionIcon
              icon="plus"
              :rotate="90"
              size="xx-small"
              colour="black"
            />
          </button>
        </div>
      </div>
    </template>
    <VisionLink
      v-else
      theme="grey"
      is-button
      line-link
      :class="bem('remove-button')"
      @click="emit('remove-item')"
    >
      Remove
    </VisionLink>
  </div>
</template>

<script setup lang="ts">
const bem = useBEM('CartItemQtySelector')

const open = ref(false)

const props = defineProps({
  currentQty: {
    type: Number,
    required: true,
  },
  allowQtyChange: {
    type: Boolean,
    default: true,
  },
  lineId: {
    type: String,
    required: true,
  },
  soldOut: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['remove-item', 'change'])

const inputId = computed(() => {
  return props.lineId.slice(-10)
})

const showRemoveBtn = computed(() => {
  return !!(props.currentQty === 1)
})

function handleSelect($event: any) {
  handleQtyChange(Number($event.target.value))
}

function handleQtyChange(value: number) {
  if (value === 0) {
    emit('remove-item')
  } else {
    emit('change', value)
  }
}

function handleClick() {
  open.value = !open.value
}
</script>

<style lang="postcss">
.CartItemQtySelector {
  @apply relative;
}

.CartItemQtySelector__trigger-button {
  @apply bg-white border border-grey-200 rounded-md
    px-4 py-2
   flex gap-4 items-center
   transition-opacity;
}

.CartItemQtySelector__qty-button {
  @apply h-10 aspect-square flex items-center justify-center
  bg-white border-l border-grey-300 hover:bg-grey-100;

  &--minus {
    @apply border-l-0 border-r;
  }
}

.CartItemQtySelector__remove-button {
  @apply text-xs;
}

.CartItemQtySelector__selector-clip {
  @apply absolute bottom-0 right-0 overflow-hidden pointer-events-none;
}

.CartItemQtySelector__selector-container {
  @apply translate-y-full transition-transform duration-300
  flex bg-white border border-grey-300
  overflow-clip rounded-md;
}

.CartItemQtySelector__selector-label {
  @apply relative flex items-center justify-center;

  input {
    @apply text-center h-full max-w-[40px]
    cursor-pointer transition-all hover:bg-grey-100;
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
}

/** Open Modifiers  */
.CartItemQtySelector--open {
  .CartItemQtySelector__selector-clip {
    @apply pointer-events-auto
  }

  .CartItemQtySelector__selector-container {
    @apply translate-y-0;
  }

  .CartItemQtySelector__trigger-button {
    @apply opacity-0;
  }
}
</style>
